import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createChart, IChartApi, ISeriesApi, CandlestickData, LineData, UTCTimestamp } from 'lightweight-charts';
import Loader from './Loader';
import { formatPriceWithAssetDecimals, getAssetDecimalPlaces } from '../utils/priceFormatter';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { useTheme } from '../contexts/ThemeContext';
import { useAITrading } from '../contexts/AITradingContext';

interface TradingChartProps {
  symbol: string;
  timeframe: string;
  chartType: string;
  onPriceUpdate?: (price: string, change: string, isPositive: boolean) => void;
  onKlinePriceUpdate?: (formattedPrice: string, closePrice: number, volume: number) => void;
  isRightPanelVisible?: boolean;
}

interface BinanceKline {
  [0]: number; // Open time
  [1]: string; // Open
  [2]: string; // High
  [3]: string; // Low
  [4]: string; // Close
  [5]: string; // Volume
  [6]: number; // Close time
  [7]: string; // Quote asset volume
  [8]: number; // Number of trades
  [9]: string; // Taker buy base asset volume
  [10]: string; // Taker buy quote asset volume
  [11]: string; // Ignore
}

const TradingChart: React.FC<TradingChartProps> = ({
  symbol,
  timeframe,
  chartType,
  onPriceUpdate,
  onKlinePriceUpdate,
}) => {
  // Volume bar configuration - set to false to disable volume bars
  const SHOW_VOLUME_BARS = false;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const greenVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const redVolumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSymbolChanging, setIsSymbolChanging] = useState(false);
  const [currentPrice, setCurrentPrice] = useState<string>('0.00');
  const [priceChange, setPriceChange] = useState<string>('+0.00%');
  const [isPositiveChange, setIsPositiveChange] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const [chartData, setChartData] = useState<CandlestickData[]>([]);
  const [volumeData, setVolumeData] = useState<LineData[]>([]);
  const [greenVolumeData, setGreenVolumeData] = useState<LineData[]>([]);
  const [redVolumeData, setRedVolumeData] = useState<LineData[]>([]);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [hasUserPositionedChart, setHasUserPositionedChart] = useState(false);
  const [prevSymbol, setPrevSymbol] = useState(symbol);

  // Use Binance market data hook for real-time updates
  const { getMarketDataForSymbol, isConnected: isBinanceConnected } = useBinanceMarketData();
  
  // Use theme context
  const { theme } = useTheme();

  // Use AI trading context for background trading
  const { updatePriceForAI } = useAITrading();

  // Reset chart function (like TradingView reset) - but don't auto-scroll to right edge
  const resetChart = useCallback(() => {
    if (chartRef.current && chartData.length > 0) {
      try {
        const timeScale = chartRef.current.timeScale();
        
        // Don't use fitContent() as it automatically scrolls to the right edge
        // Instead, set a reasonable visible range without forcing position
        
        // Set reasonable visible range to prevent excessive automatic scaling
        const lastBarTime = chartData[chartData.length - 1].time;
        // Show a reasonable number of bars based on data length
        let visibleBars;
        if (chartData.length <= 50) {
          visibleBars = chartData.length;
        } else if (chartData.length <= 200) {
          visibleBars = Math.floor(chartData.length * 0.8);
        } else {
          visibleBars = Math.floor(chartData.length * 0.6);
        }
        const firstVisibleTime = chartData[Math.max(0, chartData.length - visibleBars)].time;
        
        // Only set visible range if it's not already set or if user hasn't manually positioned
        const currentRange = timeScale.getVisibleRange();
        if (!currentRange && !hasUserPositionedChart) {
          timeScale.setVisibleRange({
            from: firstVisibleTime as UTCTimestamp,
            to: lastBarTime as UTCTimestamp,
          });
        }
        
        // Reset bar spacing to balanced values
        timeScale.applyOptions({
          barSpacing: 6, // Standard TradingView spacing
          minBarSpacing: 0.5, // More flexible for better interaction
        });
      } catch (error) {
        console.warn('Error resetting chart:', error);
      }
    }
  }, [chartData, hasUserPositionedChart]);

  // Add keyboard shortcut for reset (like TradingView)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Reset chart on Home key or Ctrl+R
      if (event.key === 'Home' || (event.ctrlKey && event.key === 'r')) {
        event.preventDefault();
        resetChart();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [resetChart]);

  // Determine if symbol is crypto
  const isCrypto = symbol.includes('USDT') || symbol.includes('BTC') || symbol.includes('ETH') || 
                   symbol.includes('SOL') || symbol.includes('ADA') || symbol.includes('DOT') || 
                   symbol.includes('LINK') || symbol.includes('DOGE') || symbol.includes('XRP') ||
                   symbol.includes('BCH') || symbol.includes('DASH') || symbol.includes('LTC') ||
                   symbol.includes('XLM') || symbol.includes('XMR');

  // Convert timeframe to Binance interval
  const getBinanceInterval = useCallback((tf: string): string => {
    const normalizedTf = tf.toLowerCase();
    
    switch (normalizedTf) {
      case '1m': return '1m';
      case '5m': return '5m';
      case '15m': return '15m';
      case '30m': return '30m';
      case '1h': return '1h';
      case '4h': return '4h';
      case '6h': return '6h';
      case '12h': return '12h';
      case '1d': return '1d';
      case '1w': return '1w';
      case '1M': return '1M';
      case '2M': return '2M';
      case '3M': return '3M';
      case '6M': return '6M';
      case '1Y': return '1y';
      default: 
        console.warn(`Unknown timeframe: ${tf}, defaulting to 1h`);
        return '1h';
    }
  }, []);

  // Fetch crypto data from Binance
  const fetchCryptoData = useCallback(async (skipChartUpdate = false) => {
    try {
      const binanceInterval = getBinanceInterval(timeframe);
      
      // For crypto symbols, use the symbol directly (already in USDT format)
      let symbolPair = symbol;
      if (isCrypto) {
        symbolPair = symbol;
      }
      

      
      const response = await fetch(`https://api.binance.com/api/v3/klines?symbol=${symbolPair}&interval=${binanceInterval}&limit=500`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!Array.isArray(data)) {
        throw new Error(`Invalid response format: expected array, got ${typeof data}`);
      }

      const klines: BinanceKline[] = data;
      
      if (klines.length > 0) {
        const latestKline = klines[klines.length - 1];
        const previousKline = klines[klines.length - 2];
        
        // Extract candlestick data
        const openPrice = parseFloat(latestKline[1]);
        const highPrice = parseFloat(latestKline[2]);
        const lowPrice = parseFloat(latestKline[3]);
        const closePrice = parseFloat(latestKline[4]);
        const volume = parseFloat(latestKline[5]);
        
        // Calculate price change and percentage
        if (previousKline) {
          const previousClose = parseFloat(previousKline[4]);
          const change = closePrice - previousClose;
          const changePercent = (change / previousClose) * 100;
          
          const formattedPrice = formatPriceWithAssetDecimals(closePrice, symbol);
          setCurrentPrice(formattedPrice);
          setPriceChange(`${change >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`);
          setIsPositiveChange(change >= 0);
          setLastUpdateTime(new Date().toLocaleTimeString());

          // Update AI trading service with current price
          updatePriceForAI(symbol, formattedPrice);

          onPriceUpdate?.(formattedPrice, `${change >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`, change >= 0);
        }
        
        // Call the kline price update callback
        const formattedPrice = formatPriceWithAssetDecimals(closePrice, symbol);
        onKlinePriceUpdate?.(formattedPrice, closePrice, volume);
        
        // Process chart data
        const processedChartData: CandlestickData[] = klines.map((kline) => ({
          time: (kline[0] / 1000) as UTCTimestamp,
          open: parseFloat(kline[1]),
          high: parseFloat(kline[2]),
          low: parseFloat(kline[3]),
          close: parseFloat(kline[4]),
        }));
        
        // Process volume data with color information
        const greenVolumeData: LineData[] = [];
        const redVolumeData: LineData[] = [];
        
        klines.forEach((kline) => {
          const open = parseFloat(kline[1]);
          const close = parseFloat(kline[4]);
          const volume = parseFloat(kline[5]);
          const isGreen = close >= open;
          
          const volumePoint = {
            time: (kline[0] / 1000) as UTCTimestamp,
            value: volume,
          };
          
          if (isGreen) {
            greenVolumeData.push(volumePoint);
          } else {
            redVolumeData.push(volumePoint);
          }
        });
        
        setChartData(processedChartData);
        
        // Store separate volume data for green and red series
        setGreenVolumeData(greenVolumeData);
        setRedVolumeData(redVolumeData);
        
        // Update chart if it exists - only update new data to preserve chart position
        if (candlestickSeriesRef.current && !skipChartUpdate) {
          try {
            
            // Get the last existing data point to avoid resetting chart position
            const lastExistingTime = chartData.length > 0 ? chartData[chartData.length - 1].time : 0;
            
            // Only add new data points, don't replace entire dataset
            const newChartData = processedChartData.filter(item => {
              // Ensure time is a number for comparison
              const itemTime = typeof item.time === 'number' ? item.time : Number(item.time);
              const lastTime = typeof lastExistingTime === 'number' ? lastExistingTime : Number(lastExistingTime);
              return itemTime > lastTime;
            });
            
            if (newChartData.length > 0) {
              // Convert new data to appropriate format for different chart types
              let newSeriesData: any[] = [];
              
              switch (chartType) {
                case 'line':
                case 'area':
                  newSeriesData = newChartData.map(candle => ({
                    time: candle.time,
                    value: candle.close,
                  }));
                  break;
                  
                case 'bar':
                  newSeriesData = newChartData.map(candle => ({
                    time: candle.time,
                    value: candle.close,
                  }));
                  break;
                  
                case 'histogram':
                  newSeriesData = newChartData.map(candle => ({
                    time: candle.time,
                    value: candle.close,
                  }));
                  break;
                  
                case 'candlestick':
                default:
                  newSeriesData = newChartData;
                  break;
              }
              
              // Only update the last data point to minimize chart disruption
              const lastDataPoint = newSeriesData[newSeriesData.length - 1];
              if (lastDataPoint) {
                candlestickSeriesRef.current.update(lastDataPoint);
              }
              
              // Update volume data only for candlestick charts
              if (chartType === 'candlestick' && SHOW_VOLUME_BARS && greenVolumeSeriesRef.current && redVolumeSeriesRef.current) {
                const newGreenVolumeData = greenVolumeData.filter(item => item.time > lastExistingTime);
                const newRedVolumeData = redVolumeData.filter(item => item.time > lastExistingTime);
                
                if (newGreenVolumeData.length > 0) {
                  greenVolumeSeriesRef.current.update(newGreenVolumeData[newGreenVolumeData.length - 1]);
                }
                if (newRedVolumeData.length > 0) {
                  redVolumeSeriesRef.current.update(newRedVolumeData[newRedVolumeData.length - 1]);
                }
              }
            }

          } catch (error) {
            console.warn('Error updating chart data:', error);
          }
        }
      }
      
      setIsLoading(false);
      setIsSymbolChanging(false);
      return data;
    } catch (err) {
      console.error('Error fetching crypto data:', err);
      setIsLoading(false);
      setIsSymbolChanging(false);
      return [];
    }
  }, [symbol, timeframe, isCrypto, onPriceUpdate, onKlinePriceUpdate, getBinanceInterval]);

  // Initialize lightweight-charts
  const initializeChart = useCallback(() => {
    if (!chartContainerRef.current) return;

    // Clean up existing chart safely
    if (chartRef.current) {
      try {
        chartRef.current.remove();
      } catch (error) {
        console.warn('Error removing existing chart:', error);
      }
      chartRef.current = null;
      candlestickSeriesRef.current = null;
      greenVolumeSeriesRef.current = null;
      redVolumeSeriesRef.current = null;
    }

    // Clear container
    chartContainerRef.current.innerHTML = '';

    // Create new chart with theme-aware colors
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: {
        background: { color: theme === 'dark' ? '#1e293b' : '#ffffff' },
        textColor: theme === 'dark' ? '#88909f' : '#6b7280', // More muted text color
      },
      grid: {
        vertLines: { color: theme === 'dark' ? '#1e293b' : '#f9fafb', visible: false }, // Hide vertical grid lines
        horzLines: { color: theme === 'dark' ? '#1e293b' : '#f9fafb', visible: false }, // Hide horizontal grid lines
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: theme === 'dark' ? '#475569' : '#d1d5db', // More subtle crosshair color
          width: 1,
          style: 2,
        },
        horzLine: {
          color: theme === 'dark' ? '#475569' : '#d1d5db', // More subtle crosshair color
          width: 1,
          style: 2,
        },
      },
      rightPriceScale: {
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
        autoScale: true,
        alignLabels: true,
        entireTextOnly: false,
        visible: true,
        ticksVisible: true,
        borderVisible: true,
      },
      timeScale: {
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 5, // Match TradingView default - no right offset
        barSpacing: 6, // Standard TradingView spacing
        minBarSpacing: 0.1, // More flexible for better interaction

        lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll: false, // Disable to allow free dragging without snapping back
        shiftVisibleRangeOnNewBar: false, // Keep disabled to prevent focus jumping
        fixLeftEdge: false,
        fixRightEdge: false,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true, // Re-enable axis scaling for user interaction
        mouseWheel: true, // Re-enable mouse wheel zoom for user interaction
        pinch: true, // Re-enable pinch zoom for user interaction
      },
    });

    // Create chart series based on chartType
    let mainSeries: any = null;
    
    switch (chartType) {
      case 'line':
        mainSeries = chart.addLineSeries({
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lineWidth: 2,
          crosshairMarkerVisible: true,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        break;
        
      case 'area':
        mainSeries = chart.addAreaSeries({
          lineColor: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          topColor: theme === 'dark' ? '#059669' : '#10b981',
          bottomColor: theme === 'dark' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(16, 185, 129, 0.1)', // More muted area fill
          lineWidth: 2,
          crosshairMarkerVisible: true,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        break;
        
      case 'bar':
        mainSeries = chart.addHistogramSeries({
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lastValueVisible: false,
          priceLineVisible: false,
        });
        break;
        
      case 'histogram':
        mainSeries = chart.addHistogramSeries({
          color: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          lastValueVisible: false,
          priceLineVisible: false,
          priceFormat: {
            type: 'volume',
          },
        });
        break;
        
      case 'candlestick':
      default:
        mainSeries = chart.addCandlestickSeries({
          upColor: theme === 'dark' ? '#059669' : '#10b981', // More muted green
          downColor: theme === 'dark' ? '#dc2626' : '#ef4444', // More muted red
          borderDownColor: theme === 'dark' ? '#dc2626' : '#ef4444',
          borderUpColor: theme === 'dark' ? '#059669' : '#10b981',
          wickDownColor: theme === 'dark' ? '#dc2626' : '#ef4444',
          wickUpColor: theme === 'dark' ? '#059669' : '#10b981',
          // Enhanced styling for better visual appearance
          borderVisible: true,
          wickVisible: true,
          // Price formatting
          priceFormat: {
            type: 'price',
            precision: 2,
            minMove: 0.01,
          },
        });
        break;
    }

    // Create separate volume series for green and red volumes (only for candlestick)
    let greenVolumeSeries: any = null;
    let redVolumeSeries: any = null;
    
    if (chartType === 'candlestick' && SHOW_VOLUME_BARS) {
      greenVolumeSeries = chart.addHistogramSeries({
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        lastValueVisible: false,
        priceLineVisible: false,
        color: 'rgba(38, 166, 154, 0.5)', // Green volume with 40% opacity
        baseLineWidth: 1, // Make volume bars very thin (histogram style)
        priceLineWidth: 1, // Thin outline
      });
      
      redVolumeSeries = chart.addHistogramSeries({
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
        lastValueVisible: false,
        priceLineVisible: false,
        color: 'rgba(240, 69, 66, 0.4)', // Red volume with 40% opacity
        baseLineWidth: 1, // Make volume bars very thin (histogram style)
        priceLineWidth: 1, // Thin outline
      });

      // Set volume series position and styling with theme-aware colors
      greenVolumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        textColor: theme === 'dark' ? '#e2e8f0' : '#1f2937',
      });
      
      redVolumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
        borderColor: theme === 'dark' ? '#334155' : '#d1d5db',
        textColor: theme === 'dark' ? '#e2e8f0' : '#1f2937',
      });

      // Apply histogram-style thin bar styling to volume series
      greenVolumeSeries.applyOptions({
        baseLineWidth: 0, // Very thin bars (histogram style)
        priceLineWidth: 1, // Thin outline
      });
      
      redVolumeSeries.applyOptions({
        baseLineWidth: 0, // Very thin bars (histogram style)
        priceLineWidth: 1, // Thin outline
      });
    }

    // Store references
    chartRef.current = chart;
    candlestickSeriesRef.current = mainSeries;
    greenVolumeSeriesRef.current = greenVolumeSeries;
    redVolumeSeriesRef.current = redVolumeSeries;

    // Set initial data if available
    if (chartData.length > 0) {
      try {
        // Convert candlestick data to appropriate format for different chart types
        let seriesData: any[] = [];
        
        switch (chartType) {
          case 'line':
          case 'area':
            seriesData = chartData.map(candle => ({
              time: candle.time,
              value: candle.close,
            }));
            break;
            
          case 'bar':
            seriesData = chartData.map(candle => ({
              time: candle.time,
              value: candle.close,
            }));
            break;
            
          case 'histogram':
            seriesData = chartData.map(candle => ({
              time: candle.time,
              value: candle.close,
            }));
            break;
            
          case 'candlestick':
          default:
            seriesData = chartData;
            break;
        }
        
        mainSeries.setData(seriesData);
        
        // Set volume data only for candlestick charts
        if (chartType === 'candlestick' && SHOW_VOLUME_BARS && greenVolumeSeries && redVolumeSeries) {
          greenVolumeSeries.setData(greenVolumeData);
          redVolumeSeries.setData(redVolumeData);
        }

        // Don't auto-fit the chart to prevent automatic scrolling to right edge
        // Let the user position the chart as they want
        // setTimeout(() => {
        //   resetChart();
        // }, 100);
      } catch (error) {
        console.warn('Error setting initial chart data:', error);
      }
    }

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        try {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
          });
        } catch (error) {
          console.warn('Error resizing chart:', error);
        }
      }
    };

    // Handle user interaction events
    const handleUserInteractionStart = () => {
      setIsUserInteracting(true);
    };

    const handleUserInteractionEnd = () => {
      // Mark that user has positioned the chart
      setHasUserPositionedChart(true);
      
      // Add a longer delay before re-enabling updates to allow smooth positioning
      setTimeout(() => {
        setIsUserInteracting(false);
      }, 1000);
    };

    // Add event listeners for user interaction (with passive option for scroll-blocking events)
    if (chartContainerRef.current) {
      chartContainerRef.current.addEventListener('mousedown', handleUserInteractionStart);
      chartContainerRef.current.addEventListener('wheel', handleUserInteractionStart, { passive: true });
      chartContainerRef.current.addEventListener('touchstart', handleUserInteractionStart, { passive: true });
      
      window.addEventListener('mouseup', handleUserInteractionEnd);
      window.addEventListener('touchend', handleUserInteractionEnd);
    }

    window.addEventListener('resize', handleResize);

    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mouseup', handleUserInteractionEnd);
      window.removeEventListener('touchend', handleUserInteractionEnd);
      
      if (chartContainerRef.current) {
        chartContainerRef.current.removeEventListener('mousedown', handleUserInteractionStart);
        chartContainerRef.current.removeEventListener('wheel', handleUserInteractionStart);
        chartContainerRef.current.removeEventListener('touchstart', handleUserInteractionStart);
      }
    };
  }, [chartData, volumeData, chartType]);

  // Initialize chart when component mounts
  useEffect(() => {
    const cleanup = initializeChart();
    return cleanup;
  }, [initializeChart]);

  // Reinitialize chart when symbol, timeframe, or theme changes
  useEffect(() => {
    // Small delay to ensure previous chart is fully cleaned up
    const timer = setTimeout(() => {
      initializeChart();
      }, 100);

    return () => clearTimeout(timer);
  }, [symbol, timeframe, theme, initializeChart]);

  // Reinitialize chart when chartType or theme changes
  useEffect(() => {
    initializeChart();
  }, [chartType, theme, initializeChart]);

  // Detect symbol changes and set loading state
  useEffect(() => {
    if (prevSymbol !== symbol) {
      setIsSymbolChanging(true);
      setIsLoading(true);
      setPrevSymbol(symbol);
    }
  }, [symbol, prevSymbol]);

  // Fetch data when component mounts or symbol changes
  useEffect(() => {
    if (isCrypto) {
      fetchCryptoData();
    } else {
      setIsLoading(false);
      setIsSymbolChanging(false);
    }
  }, [symbol, isCrypto, fetchCryptoData]);

  // Real-time updates every 5 seconds - but only for initial data load
  useEffect(() => {
    if (!isCrypto) return;

    // Only fetch data once on mount, then rely on WebSocket for real-time updates
    fetchCryptoData();
  }, [isCrypto, fetchCryptoData]);

  // Conservative real-time tick updates to prevent flickering and position changes
  const updateLastCandleTick = useCallback((newPrice: number) => {
    // Don't update chart if user is interacting to prevent freezing/flickering
    if (isUserInteracting || !candlestickSeriesRef.current) return;

    try {
      // Get the current chart data without depending on state
      setChartData(prevData => {
        if (!prevData.length) return prevData;
        
        // Get the last candle data
        const lastCandle = prevData[prevData.length - 1];
        
        // Create updated candle with new close price
        const updatedCandle = {
          time: lastCandle.time,
          open: lastCandle.open,
          high: Math.max(lastCandle.high, newPrice),
          low: Math.min(lastCandle.low, newPrice),
          close: newPrice,
        };

        // Update only the last candle without triggering chart reload or position changes
        if (candlestickSeriesRef.current) {
          candlestickSeriesRef.current.update(updatedCandle);
        }
        
        // Return updated data
        const newData = [...prevData];
        newData[newData.length - 1] = updatedCandle;
        return newData;
      });
    } catch (error) {
      console.warn('Error updating last candle tick:', error);
    }
  }, [isUserInteracting]);

  // Real-time price updates from Binance WebSocket service
  useEffect(() => {
    if (!isCrypto || !isBinanceConnected) return;

    const updatePriceFromBinance = () => {
      const marketData = getMarketDataForSymbol(symbol);
      if (marketData) {
        const { price, change, isPositive } = marketData;
        
        // Extract numeric price for chart update
        const numericPrice = parseFloat(price.replace(/[,$]/g, ''));
        
        // Update price display
        setCurrentPrice(price);
        setPriceChange(change);
        setIsPositiveChange(isPositive);
        setLastUpdateTime(new Date().toLocaleTimeString());
        
        // Update chart tick smoothly without reload
        updateLastCandleTick(numericPrice);
        
        // Update AI trading service with current price
        updatePriceForAI(symbol, price);
        
        onPriceUpdate?.(price, change, isPositive);
      }
    };

    updatePriceFromBinance();
    
    const interval = setInterval(updatePriceFromBinance, 1000);
    
    return () => clearInterval(interval);
  }, [isCrypto, isBinanceConnected, symbol, getMarketDataForSymbol, onPriceUpdate, updateLastCandleTick]);

  // Cleanup chart on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-full theme-bg-primary rounded-lg text-xs ${theme === 'light' ? 'bg-white border-0 border border-gray-100' : 'border-gray-700'}`}>
        <Loader 
          size="md" 
          title="" 
          subtitle={isSymbolChanging ? `Loading ${symbol} chart data...` : "Loading chart data..."} 
        />
      </div>
    );
  }

    return (
    <div 
      className={`relative w-full h-full ${theme === 'light' ? 'bg-white border border-0 border-gray-100' : 'border-gray-700'}`}
    >
      

      {/* Chart Container */}
        <div 
          ref={chartContainerRef} 
          className="w-full h-full candlestick-chart"
          style={{
            // Add CSS for rounded corners on candlesticks
            '--candlestick-border-radius': '2px',
            // Ensure proper interaction
            pointerEvents: 'auto',
            touchAction: 'pan-x pan-y',
          } as React.CSSProperties}
        />
      
      {/* Price overlay */}
      <div className="absolute bottom-8 left-1 theme-bg-secondary bg-opacity-95 p-3 py-2 rounded-lg z-10 shadow-xs border theme-border">
        {lastUpdateTime && (
          <div className="text-xs theme-text-quaternary">
            {(() => {
              // Format today's date as "Today, 30th August, 2025"
              const now = new Date();
              const day = now.getDate();
              const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
              ];
              // Get ordinal suffix for the day
              function getOrdinal(n: number) {
                if (n > 3 && n < 21) return n + "th";
                switch (n % 10) {
                  case 1: return n + "st";
                  case 2: return n + "nd";
                  case 3: return n + "rd";
                  default: return n + "th";
                }
              }
              const formattedDate = `Today, ${getOrdinal(day)} ${monthNames[now.getMonth()]}, ${now.getFullYear()}`;
              // Format time as "8:17:14 AM"
              const formattedTime = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
              return (
                <>                
                  {formattedDate} at {lastUpdateTime}
                </>
              );
            })()}
            {/* {new Date().toLocaleDateString()} {lastUpdateTime} */}
          </div>
        )}
      </div>
      

      
      {/* Centered Watermark Logo - Theme Aware */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 opacity-60" style={{ top: '-130px' }}>
        <div className={`transform scale-100 animate-spin-slow ${theme === 'dark' ? 'opacity-20' : 'opacity-10'}`}>
          {/* Theme-aware logo - black for dark theme, white for light theme */}
          {theme === 'dark' ? (
            <img 
              src="/images/paul_d_black.svg"
              alt="Live Trading by Paul D" 
              width="350" 
              height="auto"
              className="filter drop-shadow-2xl"
            />
          ) : (
            <img 
              src="/images/paul_d_white.svg"
              alt="Live Trading by Paul D" 
              width="350" 
              height="auto"
              className="filter drop-shadow-2xl"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingChart;
